import * as SentryLibrary from '@sentry/react';
import { QueryKey } from '@tanstack/query-core';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import hash from 'object-hash';

import { addDebugBarData, mapRpcDebugBarData } from 'utility/Debug/DebugBar';
import {
    ApiCallOptions,
    GetEndpointResponseType,
} from 'utility/Request/Api/types';
import { callApi, getEndpointConfig } from 'utility/Request/callApi';

import { ApiEndpoint, EndpointTypeMap } from 'types/Request/endpoint';

type EndpointQueryOptions<
    Endpoint extends ApiEndpoint,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
> = UseQueryOptions<
    GetEndpointResponseType<EndpointTypeMap, Endpoint>,
    TError,
    GetEndpointResponseType<EndpointTypeMap, Endpoint>,
    TQueryKey
>;

type QueryOptions<
    Endpoint extends ApiEndpoint,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
> = Omit<EndpointQueryOptions<Endpoint, TError, TQueryKey>, 'queryFn'>;

export function useQueryEndpoint<
    EndPoint extends ApiEndpoint,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
>(
    endpoint: EndPoint,
    requestOptions?: ApiCallOptions<EndpointTypeMap, EndPoint>,
    queryOptions?: QueryOptions<EndPoint, TError, TQueryKey>
) {
    const endpointConfig =
        getEndpointConfig<EndPoint, TError, TQueryKey>(endpoint);
    const currentEndpointQueryOptions: QueryOptions<
        EndPoint,
        TError,
        TQueryKey
    > = {
        useErrorBoundary: true,
        refetchOnWindowFocus: false,
        ...endpointConfig?.queryOptions,
        ...queryOptions,
    };

    const queryFunction = getQueryFunction(
        endpoint,
        requestOptions,
        currentEndpointQueryOptions
    );

    return useQuery({
        queryFn: queryFunction,
        queryKey: buildQueryKey(
            endpoint,
            requestOptions,
            currentEndpointQueryOptions
        ),
        ...currentEndpointQueryOptions,
    } as EndpointQueryOptions<EndPoint, TError, TQueryKey>);
}

const getQueryFunction =
    <
        EndPoint extends ApiEndpoint,
        TError = unknown,
        TQueryKey extends QueryKey = QueryKey
    >(
        endpoint: EndPoint,
        requestOptions: ApiCallOptions<EndpointTypeMap, EndPoint> | undefined,
        currentEndpointQueryOptions: QueryOptions<EndPoint, TError, TQueryKey>
    ) =>
    async () => {
        let response;
        try {
            response = await callApi(endpoint, requestOptions);
        } catch (error: unknown) {
            if (error instanceof Error) {
                if (!currentEndpointQueryOptions?.useErrorBoundary) {
                    SentryLibrary.captureException(error);
                }
                throw new Error(error.toString());
            }
            return;
        }

        if (!response.parsedBody) {
            if (response.signal?.aborted) {
                return {} as GetEndpointResponseType<EndpointTypeMap, EndPoint>;
            } else {
                throw response;
            }
        }
        if (response?.parsedBody && 'debugBar' in response.parsedBody) {
            addDebugBarData(
                mapRpcDebugBarData(response.parsedBody.debugBar),
                endpoint
            );
        }

        return response.parsedBody;
    };

const buildQueryKey = <
    EndPoint extends ApiEndpoint,
    TError = unknown,
    TQueryKey extends QueryKey = QueryKey
>(
    endpoint: EndPoint,
    requestOptions: ApiCallOptions<EndpointTypeMap, EndPoint> | undefined,
    queryOptions: QueryOptions<EndPoint, TError, TQueryKey>
) =>
    queryOptions?.queryKey
        ? undefined
        : //prevent hash calculation if queryKey was given manually
          [
              endpoint,
              hash({
                  ...requestOptions?.requestData,
                  ...requestOptions?.queryParams,
                  ...requestOptions?.urlParams,
              }),
          ];
